import React, { useState, useEffect, useCallback, useMemo } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Row,
  Col,
  PreviewAltCard,
  Icon,
  Button,
  RSelect,
} from "../../../components/Component";
import { BarChartExample, PieChartExample, DoughnutExample } from "../../../components/Component";
import { api } from "../../../utils/api";
import { toast } from "react-toastify";
import { Badge, Spinner, Input } from "reactstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

const AnalyticsHSEPage = () => {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  
  // Stabiliser les dates initiales
  const initialStartDate = useMemo(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 12);
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);
  
  const initialEndDate = useMemo(() => {
    const date = new Date();
    date.setHours(23, 59, 59, 999);
    return date;
  }, []);
  
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);

  useEffect(() => {
    loadSites();
  }, []);

  const loadSites = async () => {
    try {
      const response = await api.getSites();
      const sitesList = Array.isArray(response) ? response : response.data || [];
      setSites(sitesList);
    } catch (error) {
      console.error("Erreur lors du chargement des sites:", error);
    }
  };

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        start_date: moment(startDate).format("YYYY-MM-DD"),
        end_date: moment(endDate).format("YYYY-MM-DD"),
      };
      if (selectedSiteId) {
        params.site_id = selectedSiteId;
      }
      const data = await api.getAnalytics(params);
      
      // Ne mettre à jour que si les données ont vraiment changé
      setAnalytics((prevAnalytics) => {
        if (prevAnalytics && JSON.stringify(prevAnalytics) === JSON.stringify(data)) {
          return prevAnalytics; // Retourner l'ancienne référence si identique
        }
        return data;
      });
    } catch (error) {
      toast.error("Erreur lors du chargement des analytics");
      console.error("Error loading analytics:", error);
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedSiteId]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const siteOptions = useMemo(() => [
    { value: null, label: "Tous les sites" },
    ...sites.map((site) => ({ value: site.id, label: site.name })),
  ], [sites]);

  const getStatusLabel = useCallback((status) => {
    const labels = {
      draft: "Brouillon",
      submitted: "Soumis",
      received: "Reçue",
      in_analysis: "En analyse",
      corrective_action_requested: "Action requise",
      action_completed: "Action réalisée",
      verified: "Vérifiée",
      in_action: "En action",
      closed: "Clôturé",
      rejected: "Rejeté",
    };
    return labels[status] || status;
  }, []);

  // Mémoriser les fonctions de label pour éviter les re-créations
  const getTypeLabel = useCallback((type) => {
    const labels = {
      positive: "Positive",
      dangerous: "Dangereuse",
      near_miss: "Presque accident",
      non_conformity: "Non-conformité",
      negative: "Négative",
    };
    return labels[type] || type;
  }, []);

  const getSeverityLabel = useCallback((severity) => {
    const labels = {
      low: "Faible",
      medium: "Moyenne",
      high: "Élevée",
    };
    return labels[severity] || severity;
  }, []);

  const getSeverityColor = (severity) => {
    const colors = {
      low: "success",
      medium: "warning",
      high: "danger",
    };
    return colors[severity] || "secondary";
  };

  const getAnomalySeverityColor = useCallback((severity) => {
    const colors = {
      critical: "danger",
      warning: "warning",
    };
    return colors[severity] || "secondary";
  }, []);

  const getAnomalyRiskLevelColor = useCallback((riskLevel) => {
    const colors = {
      low: "success",
      medium: "warning",
      high: "danger",
      unknown: "secondary",
    };
    return colors[riskLevel] || "secondary";
  }, []);

  // Préparer les données pour les graphiques - utiliser une clé stable basée sur le contenu
  const byTypeKey = analytics?.by_type ? JSON.stringify(analytics.by_type.map(item => ({ type: item.type, count: item.count }))) : null;
  const prepareTypeChartData = useMemo(() => {
    if (!analytics?.by_type || analytics.by_type.length === 0) return null;
    const byType = analytics.by_type;
    const labels = byType.map((item) => getTypeLabel(item.type));
    const data = byType.map((item) => item.count || 0);
    return {
      labels,
      dataUnit: "Observations",
      datasets: [
        {
          data,
          backgroundColor: ["#798bff", "#5ce0aa", "#ff6b6b", "#ffa726"],
        },
      ],
    };
  }, [byTypeKey, getTypeLabel]);

  const byStatusKey = analytics?.by_status ? JSON.stringify(analytics.by_status.map(item => ({ status: item.status, count: item.count }))) : null;
  const prepareStatusChartData = useMemo(() => {
    if (!analytics?.by_status || analytics.by_status.length === 0) return null;
    const byStatus = analytics.by_status;
    const labels = byStatus.map((item) => getStatusLabel(item.status));
    const data = byStatus.map((item) => item.count || 0);
    return {
      labels,
      dataUnit: "Observations",
      datasets: [
        {
          data,
          backgroundColor: ["#798bff", "#5ce0aa", "#ffa726", "#ff6b6b", "#9c27b0", "#607d8b"],
        },
      ],
    };
  }, [byStatusKey, getStatusLabel]);

  const bySeverityKey = analytics?.by_severity ? JSON.stringify(analytics.by_severity.map(item => ({ severity: item.severity, count: item.count }))) : null;
  const prepareSeverityChartData = useMemo(() => {
    if (!analytics?.by_severity || analytics.by_severity.length === 0) return null;
    const bySeverity = analytics.by_severity;
    const labels = bySeverity.map((item) => getSeverityLabel(item.severity));
    const data = bySeverity.map((item) => item.count || 0);
    return {
      labels,
      dataUnit: "Observations",
      datasets: [
        {
          data,
          backgroundColor: ["#5ce0aa", "#ffa726", "#ff6b6b"],
        },
      ],
    };
  }, [bySeverityKey, getSeverityLabel]);

  const bySiteKey = analytics?.by_site ? JSON.stringify(analytics.by_site.slice(0, 10).map(item => ({ name: item.name, count: item.count }))) : null;
  const prepareSiteChartData = useMemo(() => {
    if (!analytics?.by_site || analytics.by_site.length === 0) return null;
    const topSites = analytics.by_site.slice(0, 10);
    const labels = topSites.map((item) => item.name);
    const data = topSites.map((item) => item.count || 0);
    return {
      labels,
      dataUnit: "Observations",
      datasets: [
        {
          label: "Observations par site",
          backgroundColor: "#798bff",
          data,
        },
      ],
    };
  }, [bySiteKey]);

  if (loading && !analytics) {
    return (
      <React.Fragment>
        <Head title="Analytics HSE" />
        <Content>
          <BlockHead size="sm">
            <BlockHeadContent>
              <BlockTitle page>Analytics HSE</BlockTitle>
            </BlockHeadContent>
          </BlockHead>
          <Block>
            <div className="text-center p-4">
              <Spinner color="primary" />
              <p className="mt-2 text-soft">Chargement des données...</p>
            </div>
          </Block>
        </Content>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Head title="Analytics HSE" />
      <Content>
        <BlockHead size="sm">
          <BlockHeadContent>
            <BlockTitle page>Analytics HSE</BlockTitle>
            <BlockDes className="text-soft">
                <p>Analyses détaillées et statistiques des données HSE</p>
            </BlockDes>
          </BlockHeadContent>
            <BlockHeadContent className="mt-3">
              <ul className="nk-block-tools g-3">
                <li>
                  <div className="form-control-wrap">
                    <label className="form-label" style={{ fontSize: "11px", marginBottom: "4px", display: "block", color: "#526484", fontWeight: 500 }}>
                      Date de début
                    </label>
                    <DatePicker
                      selected={startDate}
                      onChange={(date) => setStartDate(date)}
                      selectsStart
                      startDate={startDate}
                      endDate={endDate}
                      dateFormat="dd/MM/yyyy"
                      className="form-control"
                      placeholderText="Date de début"
                    />
                  </div>
                </li>
                <li>
                  <div className="form-control-wrap">
                    <label className="form-label" style={{ fontSize: "11px", marginBottom: "4px", display: "block", color: "#526484", fontWeight: 500 }}>
                      Date de fin
                    </label>
                    <DatePicker
                      selected={endDate}
                      onChange={(date) => setEndDate(date)}
                      selectsEnd
                      startDate={startDate}
                      endDate={endDate}
                      minDate={startDate}
                      dateFormat="dd/MM/yyyy"
                      className="form-control"
                      placeholderText="Date de fin"
                    />
                  </div>
                </li>
                <li>
                  <div className="form-control-wrap" style={{ minWidth: "300px", width: "300px", display: "block" }}>
                    <label className="form-label" style={{ fontSize: "11px", marginBottom: "4px", display: "block", color: "#526484", fontWeight: 500 }}>
                      Site
                    </label>
                    <RSelect
                      options={siteOptions}
                      value={siteOptions.find(opt => opt.value === selectedSiteId) || null}
                      onChange={(option) => setSelectedSiteId(option ? option.value : null)}
                      placeholder="Sélectionner un site"
                    />
                  </div>
                </li>
              </ul>
            </BlockHeadContent>
        </BlockHead>

        {analytics && (
          <>
            {/* KPI Cards */}
            <Block>
              <Row className="g-gs">
                <Col md="6" lg="3">
                  <PreviewAltCard className="card-full">
                    <div className="card-inner">
                      <div className="card-title-group">
                        <div className="card-title">
                          <h6 className="title">Total Observations</h6>
                        </div>
                      </div>
                      <div className="card-amount">
                        <span className="amount">{analytics.overview.total}</span>
                        {analytics.overview.total_change_percent !== 0 && (
                          <span
                            className={`change ${analytics.overview.total_change_percent > 0 ? "text-danger" : "text-success"}`}
                          >
                            {analytics.overview.total_change_percent > 0 ? "+" : ""}
                            {analytics.overview.total_change_percent}%
                          </span>
                        )}
                      </div>
                      <div className="card-stats">
                        <span className="text-soft">
                          {analytics.overview.positive} positives • {analytics.overview.negative} négatives
                        </span>
                      </div>
                    </div>
                  </PreviewAltCard>
                </Col>
                <Col md="6" lg="3">
                  <PreviewAltCard className="card-full">
                    <div className="card-inner">
                      <div className="card-title-group">
                        <div className="card-title">
                          <h6 className="title">Taux de clôture</h6>
                        </div>
                      </div>
                      <div className="card-amount">
                        <span className="amount">{analytics.overview.closure_rate}%</span>
                      </div>
                      <div className="card-stats">
                        <span className="text-soft">
                          {analytics.overview.closed} clôturées • {analytics.overview.in_progress} en cours
                        </span>
                      </div>
                    </div>
                  </PreviewAltCard>
                </Col>
                <Col md="6" lg="3">
                  <PreviewAltCard className="card-full">
                    <div className="card-inner">
                      <div className="card-title-group">
                        <div className="card-title">
                          <h6 className="title">Temps moyen de traitement</h6>
                        </div>
                      </div>
                      <div className="card-amount">
                        <span className="amount">{analytics.overview.avg_processing_time_hours}h</span>
                      </div>
                      <div className="card-stats">
                        <span className="text-soft">Temps moyen par observation</span>
                      </div>
                    </div>
                  </PreviewAltCard>
                </Col>
                <Col md="6" lg="3">
                  <PreviewAltCard className="card-full">
                    <div className="card-inner">
                      <div className="card-title-group">
                        <div className="card-title">
                          <h6 className="title">Gravité élevée</h6>
                        </div>
                      </div>
                      <div className="card-amount">
                        <span className="amount">{analytics.overview.high_severity}</span>
                      </div>
                      <div className="card-stats">
                        <span className="text-soft">
                          {analytics.overview.medium_severity} moyenne • {analytics.overview.low_severity} faible
                        </span>
                      </div>
                    </div>
                  </PreviewAltCard>
                </Col>
              </Row>
            </Block>

            {/* Anomaly Detection */}
            {analytics.anomaly_detection && (
              <Block>
                <Row className="g-gs">
                  <Col lg="12">
                    <PreviewAltCard>
                      <div className="nk-block-head nk-block-head-sm">
                        <div className="nk-block-between">
                          <div className="nk-block-head-content">
                            <h6 className="title">
                              <Icon name="activity-round" className="me-2" />
                              Détection intelligente d&apos;anomalies
                            </h6>
                            <p className="text-soft small mb-0 mt-1">{analytics.anomaly_detection.message}</p>
                          </div>
                          <div className="nk-block-head-content">
                            <Badge color={getAnomalyRiskLevelColor(analytics.anomaly_detection.latest_risk_level)}>
                              Risque {analytics.anomaly_detection.latest_risk_level || "unknown"}
                            </Badge>
                          </div>
                        </div>
                      </div>

                      <div className="mb-3">
                        <span className="text-soft small">
                          Baseline: {analytics.anomaly_detection.baseline_months || 0} mois analysés
                        </span>
                        {analytics.anomaly_detection.latest_summary && (
                          <p className="small mb-0 mt-2">{analytics.anomaly_detection.latest_summary}</p>
                        )}
                      </div>

                      {analytics.anomaly_detection.status === "insufficient_data" ? (
                        <div className="text-center p-3">
                          <span className="text-soft">Pas assez d&apos;historique pour une analyse fiable.</span>
                        </div>
                      ) : Array.isArray(analytics.anomaly_detection.anomalies) &&
                        analytics.anomaly_detection.anomalies.length > 0 ? (
                        <div className="table-responsive">
                          <table className="table table-ul">
                            <thead>
                              <tr>
                                <th>Période</th>
                                <th>Métrique</th>
                                <th className="text-end">Valeur</th>
                                <th className="text-end">Z-score</th>
                                <th className="text-center">Niveau</th>
                                <th>Action recommandée</th>
                              </tr>
                            </thead>
                            <tbody>
                              {analytics.anomaly_detection.anomalies.map((a, index) => (
                                <tr key={`${a.metric}-${a.period}-${index}`}>
                                  <td>{a.period_label || a.period || "-"}</td>
                                  <td>
                                    <span className="tb-lead">{a.metric_label}</span>
                                    {a.message && <div className="text-soft small mt-1">{a.message}</div>}
                                  </td>
                                  <td className="text-end">{a.value}</td>
                                  <td className="text-end">{a.z_score}</td>
                                  <td className="text-center">
                                    <Badge color={getAnomalySeverityColor(a.severity)}>{a.severity}</Badge>
                                  </td>
                                  <td className="small">{a.recommended_action}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center p-3">
                          <span className="text-soft">Aucune anomalie détectée sur la période.</span>
                        </div>
                      )}
                    </PreviewAltCard>
                  </Col>
                </Row>
              </Block>
            )}

            {/* Trends Table */}
        <Block>
          <Row className="g-gs">
            <Col lg="12">
                  <PreviewAltCard>
                    <div className="nk-block-head">
                      <h6 className="title">Tendances mensuelles</h6>
                    </div>
                    {analytics?.trends?.monthly && analytics.trends.monthly.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-ul">
                          <thead>
                            <tr>
                              <th>Période</th>
                              <th className="text-end">Total</th>
                              <th className="text-end">Positives</th>
                              <th className="text-end">Négatives</th>
                              <th className="text-end">Clôturées</th>
                            </tr>
                          </thead>
                          <tbody>
                            {analytics.trends.monthly.map((item, index) => (
                              <tr key={index}>
                                <td>
                                  <span className="tb-lead">{item.label}</span>
                                </td>
                                <td className="text-end">
                                  <Badge color="primary">{item.total || 0}</Badge>
                                </td>
                                <td className="text-end">
                                  <Badge color="success">{item.positive || 0}</Badge>
                                </td>
                                <td className="text-end">
                                  <Badge color="danger">{item.negative || 0}</Badge>
                                </td>
                                <td className="text-end">
                                  <Badge color="info">{item.closed || 0}</Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center p-4">
                        <span className="text-soft">Aucune donnée disponible</span>
                      </div>
                    )}
                  </PreviewAltCard>
                </Col>
              </Row>
            </Block>

            {/* Charts Row */}
            <Block>
              <Row className="g-gs">
                <Col lg="6">
              <PreviewAltCard>
                <div className="nk-block-head">
                  <h6 className="title">Observations par type</h6>
                </div>
                    {prepareTypeChartData && byTypeKey ? (
                      <div className="nk-ck" key={byTypeKey}>
                        <PieChartExample data={prepareTypeChartData} />
                      </div>
                    ) : (
                      <div className="text-center p-4">
                        <span className="text-soft">Aucune donnée disponible</span>
                      </div>
                    )}
                  </PreviewAltCard>
                </Col>
                <Col lg="6">
                  <PreviewAltCard>
                    <div className="nk-block-head">
                      <h6 className="title">Observations par statut</h6>
                    </div>
                    {prepareStatusChartData ? (
                      <div className="nk-ck">
                        <DoughnutExample data={prepareStatusChartData} />
                      </div>
                    ) : (
                      <div className="text-center p-4">
                        <span className="text-soft">Aucune donnée disponible</span>
                      </div>
                    )}
                  </PreviewAltCard>
                </Col>
              </Row>
            </Block>

            {/* Severity and Sites */}
            <Block>
              <Row className="g-gs">
                <Col lg="6">
                  <PreviewAltCard>
                    <div className="nk-block-head">
                      <h6 className="title">Observations par gravité</h6>
                    </div>
                    {prepareSeverityChartData ? (
                      <div className="nk-ck">
                        <DoughnutExample data={prepareSeverityChartData} />
                      </div>
                    ) : (
                      <div className="text-center p-4">
                        <span className="text-soft">Aucune donnée disponible</span>
                      </div>
                    )}
                  </PreviewAltCard>
                </Col>
                <Col lg="6">
                  <PreviewAltCard>
                    <div className="nk-block-head">
                      <h6 className="title">Top 10 sites par observations</h6>
                    </div>
                    {prepareSiteChartData ? (
                      <div className="nk-ck">
                        <BarChartExample data={prepareSiteChartData} />
                      </div>
                    ) : (
                <div className="text-center p-4">
                        <span className="text-soft">Aucune donnée disponible</span>
                      </div>
                    )}
                  </PreviewAltCard>
                </Col>
              </Row>
            </Block>

            {/* Risk Analysis */}
            {analytics.risk_analysis?.top_risks && analytics.risk_analysis.top_risks.length > 0 && (
              <Block>
                <Row className="g-gs">
                  <Col lg="12">
                    <PreviewAltCard>
                      <div className="nk-block-head">
                        <h6 className="title">Top 10 types de risques</h6>
                      </div>
                      <div className="table-responsive">
                        <table className="table table-ul">
                          <thead>
                            <tr>
                              <th>Type de risque</th>
                              <th className="text-end">Nombre d'occurrences</th>
                            </tr>
                          </thead>
                          <tbody>
                            {analytics.risk_analysis.top_risks.map((risk, index) => (
                              <tr key={index}>
                                <td>
                                  <span className="tb-lead">{risk.risk_type || "Non spécifié"}</span>
                                </td>
                                <td className="text-end">
                                  <Badge color="primary">{risk.count}</Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </PreviewAltCard>
                  </Col>
                </Row>
              </Block>
            )}

            {/* Top Contributors */}
            {analytics.top_contributors && (
              <Block>
                <Row className="g-gs">
                  <Col lg="6">
                    <PreviewAltCard>
                      <div className="nk-block-head">
                        <h6 className="title">Top 10 contributeurs</h6>
                      </div>
                      <div className="table-responsive">
                        <table className="table table-ul">
                          <thead>
                            <tr>
                              <th>Utilisateur</th>
                              <th className="text-end">Observations</th>
                            </tr>
                          </thead>
                          <tbody>
                            {analytics.top_contributors.creators.map((creator, index) => (
                              <tr key={index}>
                                <td>
                                  <span className="tb-lead">{creator.name}</span>
                                </td>
                                <td className="text-end">
                                  <Badge color="info">{creator.count}</Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </PreviewAltCard>
                  </Col>
                  <Col lg="6">
                    <PreviewAltCard>
                      <div className="nk-block-head">
                        <h6 className="title">Top 10 sites contributeurs</h6>
                      </div>
                      <div className="table-responsive">
                        <table className="table table-ul">
                          <thead>
                            <tr>
                              <th>Site</th>
                              <th className="text-end">Observations</th>
                            </tr>
                          </thead>
                          <tbody>
                            {analytics.top_contributors.sites.map((site, index) => (
                              <tr key={index}>
                                <td>
                                  <span className="tb-lead">{site.name}</span>
                                </td>
                                <td className="text-end">
                                  <Badge color="success">{site.count}</Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                </div>
              </PreviewAltCard>
            </Col>
          </Row>
        </Block>
            )}
          </>
        )}
      </Content>
    </React.Fragment>
  );
};

export default AnalyticsHSEPage;
